// ============================================================================
// ClientLoginPage — route /me/login (public). The client signs in with the name
// their trainer uses + the password they set on claim (POST /api/client/login
// via the client auth context). Mobile-first, Pulse, EN + 繁中. Bounces an
// already-signed-in client to wherever they were headed (or the portal home).
// ============================================================================
import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banner, Button, Card, TextField } from "../../components";
import { ApiError } from "../../api";
import { useClientAuth } from "../auth/ClientAuthContext";
import { ClientLanguageToggle } from "../ClientLanguageToggle";
import { ClientAuthBrand } from "./ClientAuthBrand";
import { CLIENT_FORGOT_PATH, CLIENT_HOME_PATH } from "../routes";

interface FromState {
  from?: { pathname?: string };
}

export function ClientLoginPage() {
  const { t } = useTranslation("clientPortal");
  const { login, isAuthenticated, isLoading } = useClientAuth();
  const location = useLocation();
  const dest =
    (location.state as FromState | null)?.from?.pathname ?? CLIENT_HOME_PATH;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isLoading && isAuthenticated) return <Navigate to={dest} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ username, password });
      // isAuthenticated flips; the redirect above takes over on next render.
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) setError(t("login.lockedOut"));
        else if (err.status === 401) setError(t("login.invalid"));
        else setError(t("login.genericError"));
      } else {
        setError(t("login.genericError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-body">
      <header className="mx-auto flex max-w-md items-center justify-between px-5 py-5">
        <ClientAuthBrand />
        <ClientLanguageToggle />
      </header>
      <main className="mx-auto flex max-w-md flex-col px-5 pb-16 pt-2">
        <Card className="space-y-5">
          <div>
            <p className="font-display text-xs font-bold uppercase tracking-kicker text-coral">
              {t("login.kicker")}
            </p>
            <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tightest text-body">
              {t("login.title")}
            </h1>
            <p className="mt-1.5 text-body-soft">{t("login.blurb")}</p>
          </div>

          {error && <Banner tone="danger" title={error} />}

          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <TextField
              label={t("login.name")}
              autoComplete="username"
              placeholder={t("login.namePlaceholder")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              disabled={submitting}
            />
            <TextField
              label={t("login.password")}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
            />
            <div className="-mt-1 text-right">
              <Link
                to={CLIENT_FORGOT_PATH}
                className="text-sm font-bold text-body-soft transition-colors hover:text-coral"
              >
                {t("login.forgotPassword")}
              </Link>
            </div>
            <Button type="submit" block size="lg" disabled={submitting}>
              {submitting ? t("login.signingIn") : t("login.submit")}
            </Button>
          </form>

          <p className="text-center text-sm text-body-faint">
            {t("login.needInvite")}
          </p>
        </Card>
      </main>
    </div>
  );
}
